/**
 * A team name that never ellipsizes (SMOKE-3, Scores UX spec section 1.4).
 * It shows the longest server name that fits the measured box, the way score
 * rows do: the full name ("Carolina Panthers"), then the server's shortName
 * ("Panthers"), then the abbreviation ("CAR"). The box is measured with a
 * ResizeObserver; before the first measurement (and where layout is not
 * available) the full name shows. The accessible name is always the full name.
 */
import { pickTeamLabel } from "@sportsmet/theme/scores";
import { cn } from "@sportsmet/ui";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

export function FittedTeamName({
  name,
  shortName = null,
  abbreviation,
  fontPx,
  className,
  testId,
}: {
  name: string;
  shortName?: string | null;
  abbreviation: string;
  /** The rendered font size, for the width estimate. */
  fontPx: number;
  className?: string;
  testId?: string;
}): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [boxPx, setBoxPx] = useState(Number.POSITIVE_INFINITY);
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const measure = (): void => {
      const width = element.getBoundingClientRect().width;
      // A box not laid out yet keeps the full name rather than collapsing
      // every label to its abbreviation.
      if (width > 0) setBoxPx(width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  const label = pickTeamLabel({ name, shortName, abbreviation }, boxPx, fontPx);
  return (
    <span
      ref={ref}
      className={cn(
        "min-w-0 flex-1 overflow-hidden whitespace-nowrap",
        className,
      )}
      data-testid={testId}
      aria-label={label === name ? undefined : name}
      title={label === name ? undefined : name}
    >
      {label}
    </span>
  );
}
